
import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { FieldArrayType, FormlyModule } from '@ngx-formly/core';
import { ApplicationFacade } from '@intellio/shared/state-mgmt';
import { take } from 'rxjs/operators';

@Component({
  selector: 'itc-formly-repeat-section',
  styles: ['button {margin-bottom: 20px;}'],
  template: `
    @for (field of field.fieldGroup; track field; let i = $index) {
      <div class="row">
        <formly-field class="col w-100" [field]="field"></formly-field>
        @if (!props.hideRemove) {
        <div>
          <button
            type="button"
            tabindex="-1"
            mat-icon-button
            [disabled]="!canRemoveItem()"
            (click)="remove(i)"
          >
            <mat-icon>delete</mat-icon>
          </button>
        </div>
        }
      </div>
    }
    @if (!props.hideAdd) {
    <div>
      <button
        type="button"
        id="repeat-button"
        mat-stroked-button
        color="primary"
        (click)="add()"
        >
        {{ to.addText }}
      </button>
    </div>
    }
    @if (showError) {
      <div class="ng-error" [style.display]="'block'">
        <formly-validation-message [field]="field"></formly-validation-message>
      </div>
    }
    `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormlyModule, MatIconModule, MatButtonModule],
})
export class RepeatSectionTypeComponent extends FieldArrayType implements OnInit {
  constructor(private readonly applicationFacade?: ApplicationFacade) {
    super();
  }

  ngOnInit(): void {
    this.populateFromApplication();
  }

  populateFromApplication(): void {
    // This opt-in prop identifies a repeatable collection on the application
    // that should seed an otherwise empty repeat section.
    const sourcePath = this.props.populateFromApplication as string | undefined;

    // Do not overwrite rows loaded from saved form data.
    if (!sourcePath || !this.applicationFacade || this.field.fieldGroup?.length) {
      return;
    }

    // Use the current application snapshot only; subsequent application edits
    // must not silently replace the initialized rows.
    this.applicationFacade.currentApplication$.pipe(take(1)).subscribe((app) => {
      // Resolve the configured dot-delimited path without adding schema-specific logic.
      const sourceItems = sourcePath
        .split('.')
        .reduce((value, pathSegment) => value?.[pathSegment], app as any);

      if (!Array.isArray(sourceItems) || sourceItems.length === 0) {
        // There are no source records to render, so omit the empty section entirely.
        this.field.hide = true;
        return;
      }

      // FieldArrayType.add creates the Formly field and FormArray control for
      // each item; copying the model alone would not create repeat rows.
      sourceItems.forEach((sourceItem) =>
        this.add(undefined, sourceItem, { markAsDirty: false })
      );
    });
  }

  canRemoveItem(): boolean {
    const minimumItems = Math.max(
      Number((this.props as { minItems?: number }).minItems) || 0,
      0
    );

    return (this.field.fieldGroup?.length ?? 0) > minimumItems;
  }

  override remove(index: number): void {
    if (this.canRemoveItem()) {
      super.remove(index);
    }
  }
}
